import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'

function MyHitLists() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [lists, setLists] = useState([])
  const [loading, setLoading] = useState(true)

  // Single form state — used for both create and edit
  const [formMode, setFormMode] = useState(null) // null | 'create' | 'edit'
  const [editingListId, setEditingListId] = useState(null)
  const [formTitle, setFormTitle] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [formIsPublic, setFormIsPublic] = useState(true)
  const [formIsOpen, setFormIsOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!user) { navigate('/login'); return }
    fetchMyLists()
  }, [user])

  const fetchMyLists = async () => {
    try {
      const { data, error } = await supabase
        .from('hitlists')
        .select('*, hitlist_items (item_id)')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      setLists((data || []).map(l => ({ ...l, itemCount: l.hitlist_items?.length || 0 })))
      setLoading(false)
    } catch (err) {
      console.error(err)
      setLoading(false)
    }
  }

  const openCreateForm = () => {
    setFormTitle('')
    setFormDescription('')
    setFormIsPublic(true)
    setFormIsOpen(false)
    setError('')
    setEditingListId(null)
    setFormMode('create')
  }

  const openEditForm = (list) => {
    setFormTitle(list.title)
    setFormDescription(list.description || '')
    setFormIsPublic(list.is_public)
    setFormIsOpen(list.is_open_contributions)
    setError('')
    setEditingListId(list.hitlist_id)
    setFormMode('edit')
  }

  const closeForm = () => {
    setFormMode(null)
    setEditingListId(null)
    setError('')
  }

  const handleCreate = async () => {
    if (!formTitle.trim()) { setError('Title is required.'); return }
    setSaving(true)
    setError('')
    try {
      const { data, error } = await supabase
        .from('hitlists')
        .insert([{
          user_id: user.id,
          title: formTitle.trim(),
          description: formDescription.trim() || null,
          is_public: formIsPublic,
          is_open_contributions: formIsOpen
        }])
        .select()

      if (error) throw error
      navigate(`/hitlists/${data[0].hitlist_id}`)
    } catch (err) {
      setError(err.message || 'Failed to create HitList.')
      setSaving(false)
    }
  }

  const handleUpdate = async () => {
    if (!formTitle.trim()) { setError('Title is required.'); return }
    setSaving(true)
    setError('')
    try {
      const { error } = await supabase
        .from('hitlists')
        .update({
          title: formTitle.trim(),
          description: formDescription.trim() || null,
          is_public: formIsPublic,
          is_open_contributions: formIsOpen
        })
        .eq('hitlist_id', editingListId)

      if (error) throw error
      closeForm()
      await fetchMyLists()
    } catch (err) {
      setError(err.message || 'Failed to update HitList.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (hitlistId, title) => {
    if (!window.confirm(`Delete "${title}"? This will remove all lures from the list.`)) return
    try {
      const { error } = await supabase.from('hitlists').delete().eq('hitlist_id', hitlistId)
      if (error) throw error
      if (editingListId === hitlistId) closeForm()
      await fetchMyLists()
    } catch (err) {
      console.error(err)
      alert('Failed to delete HitList.')
    }
  }

  if (loading) return <div className="loading">Loading your HitLists...</div>

  // Inline form JSX — NOT a nested component function (avoids re-mount-on-keypress bug)
  const formJSX = formMode && (
    <div className="hitlist-form">
      <h3>{formMode === 'edit' ? 'Edit HitList Settings' : 'Create a New HitList'}</h3>

      {error && <div className="alert-error" style={{ marginBottom: '1rem' }}>{error}</div>}

      <div className="form-group">
        <label><strong>Title *</strong></label>
        <input
          type="text"
          value={formTitle}
          onChange={e => setFormTitle(e.target.value)}
          className="form-input"
          placeholder="e.g. Lake Erie Fall Walleye Killers"
          maxLength={200}
          autoFocus
        />
      </div>

      <div className="form-group">
        <label><strong>Description (Optional)</strong></label>
        <textarea
          value={formDescription}
          onChange={e => setFormDescription(e.target.value)}
          className="form-textarea"
          rows="3"
          placeholder="What's this list about?"
        />
      </div>

      <div className="form-group">
        <label><strong>Visibility</strong></label>
        <div className="radio-group">
          <label className="radio-label">
            <input
              type="radio"
              name={`visibility-${formMode}`}
              checked={formIsPublic}
              onChange={() => setFormIsPublic(true)}
            />
            <div>
              <span>🌐 Public</span>
              <small>Anyone can find and view this list</small>
            </div>
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name={`visibility-${formMode}`}
              checked={!formIsPublic}
              onChange={() => setFormIsPublic(false)}
            />
            <div>
              <span>🔒 Private</span>
              <small>Only you can see it</small>
            </div>
          </label>
        </div>
      </div>

      <div className="form-group">
        <label className="toggle-label">
          <input
            type="checkbox"
            checked={formIsOpen}
            onChange={e => setFormIsOpen(e.target.checked)}
          />
          <div>
            <span>👥 Open Contributions</span>
            <small>Allow other logged-in users to add lures to this list</small>
          </div>
        </label>
      </div>

      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={closeForm} disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={formMode === 'edit' ? handleUpdate : handleCreate}
          disabled={saving}
        >
          {saving ? 'Saving...' : (formMode === 'edit' ? 'Save Changes' : 'Create HitList')}
        </button>
      </div>
    </div>
  )

  return (
    <div className="my-hitlists-page">
      <div className="page-header">
        <div>
          <h1>My HitLists</h1>
          <p className="page-subtitle">Create and manage your curated lure lists</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <Link to="/hitlists" className="btn-secondary">Browse Public Lists</Link>
          {formMode !== 'create' && (
            <button className="btn-primary" onClick={openCreateForm}>
              + New HitList
            </button>
          )}
        </div>
      </div>

      {/* Form renders here for create mode, or inline on the card for edit mode */}
      {formMode === 'create' && formJSX}

      {lists.length === 0 && formMode !== 'create' ? (
        <div className="no-content-box">
          <p>You haven't created any HitLists yet.</p>
          <button className="btn-primary" style={{ marginTop: '1rem' }} onClick={openCreateForm}>
            Create Your First HitList
          </button>
        </div>
      ) : (
        <div className="my-hitlists-list">
          {lists.map(list => (
            <div key={list.hitlist_id} className="my-hitlist-card">
              {formMode === 'edit' && editingListId === list.hitlist_id ? (
                formJSX
              ) : (
                <>
                  <div className="my-hitlist-header">
                    <div className="my-hitlist-info">
                      <h3>
                        <Link to={`/hitlists/${list.hitlist_id}`}>{list.title}</Link>
                      </h3>
                      <div className="hitlist-badges" style={{ marginTop: '0.4rem' }}>
                        <span className={`hitlist-privacy-badge ${list.is_public ? 'public' : 'private'}`}>
                          {list.is_public ? '🌐 Public' : '🔒 Private'}
                        </span>
                        {list.is_open_contributions && (
                          <span className="hitlist-privacy-badge open">👥 Open</span>
                        )}
                        <span className="hitlist-item-count">{list.itemCount} lures</span>
                      </div>
                      {list.description && (
                        <p className="my-hitlist-description">{list.description}</p>
                      )}
                    </div>
                    <div className="my-hitlist-actions">
                      <Link to={`/hitlists/${list.hitlist_id}`} className="btn-primary btn-sm">
                        View &amp; Manage Lures
                      </Link>
                      <button
                        className="btn-secondary btn-sm"
                        onClick={() => openEditForm(list)}
                      >
                        ⚙️ Edit Settings
                      </button>
                      <button
                        className="btn-reject btn-sm"
                        onClick={() => handleDelete(list.hitlist_id, list.title)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="my-hitlist-footer">
                    Created {new Date(list.created_at).toLocaleDateString()}
                    {list.updated_at !== list.created_at && (
                      <span> · Updated {new Date(list.updated_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default MyHitLists
